import React from 'react';
import { Minus, Plus, Trash2, Loader2, RotateCw, Trophy } from 'lucide-react';

const START_OPTIONS = [
  { key: 'nybegynner', label: 'Nybegynner' },
  { key: 'bronse', label: 'Bronse' },
];

const LEVELS = ['nybegynner', 'viderekommende', 'bronse', 'solv', 'gull'];

const LEVEL_LABELS = { nybegynner: 'Nybegynner', viderekommende: 'Viderekommende', bronse: 'Bronse', solv: 'Sølv', gull: 'Gull' };

// Simulerer økten: krav per serie + status etter tastede poeng (neste nivå etter 10 godkjente)
const simulateSession = (entry, variant) => {
  const empty = { thresholds: entry.scores.map(() => null), idx: null, count: 0, achieved: [] };
  if (!variant?.requirements || !entry.variant_key || entry.statusLoading) return empty;
  const st = entry.status?.variants?.[`${entry.discipline_key}:${entry.variant_key}`];
  if (st?.completed) return { ...empty, idx: LEVELS.length };
  let idx = st ? LEVELS.indexOf(st.active_level) : LEVELS.indexOf(entry.start_level || 'nybegynner');
  let count = st ? (st.count || 0) : 0;
  const achieved = [];
  const thresholds = entry.scores.map((s) => {
    if (idx < 0 || idx >= LEVELS.length) return null;
    const thr = variant.requirements[LEVELS[idx]];
    if (s !== '' && s != null && Number(s) >= thr) {
      count += 1;
      if (count >= 10) { achieved.push(LEVELS[idx]); idx += 1; count = 0; }
    }
    return thr;
  });
  return { thresholds, idx, count, achieved };
};

const BadgeCountdown = ({ sim, id }) => {
  if (sim.idx == null) return null;
  if (sim.idx >= LEVELS.length) {
    return <p className="text-xs text-yellow-300 font-medium" data-testid={`entry-countdown-${id}`}>Alle nivåer fullført</p>;
  }
  const next = LEVEL_LABELS[LEVELS[sim.idx]];
  const left = 10 - sim.count;
  const last = sim.achieved[sim.achieved.length - 1];
  return (
    <div className="space-y-1" data-testid={`entry-countdown-${id}`}>
      <p className="text-xs text-gray-200">
        {last && <span className="text-green-300 font-semibold">{LEVEL_LABELS[last]} klart i denne økten! </span>}
        <strong className="text-white">{left} {left === 1 ? 'serie' : 'serier'} igjen</strong> til {next}
        <span className="text-gray-400"> ({sim.count}/10)</span>
      </p>
      <div className="h-1.5 rounded-full bg-gray-700 overflow-hidden">
        <div className="h-full bg-yellow-500 transition-[width] duration-300" style={{ width: `${sim.count * 10}%` }} />
      </div>
    </div>
  );
};

const resizeScores = (scores, n) => {
  const nn = Math.max(1, Math.min(20, n));
  return Array(nn).fill('').map((_, i) => scores[i] || '');
};

const SeriesStepper = ({ value, onChange, testId }) => (
  <div className="flex items-center gap-1 shrink-0" data-testid={testId}>
    <span className="text-[11px] text-gray-400 mr-1 hidden sm:inline">Serier</span>
    <button type="button" onClick={() => onChange(value - 1)} disabled={value <= 1}
      className="w-10 h-10 rounded-lg bg-gray-700 border border-gray-600 text-white flex items-center justify-center active:bg-gray-600 disabled:opacity-40 touch-manipulation"
      aria-label="Færre serier" data-testid={`${testId}-minus`}>
      <Minus className="w-4 h-4" />
    </button>
    <span className="w-8 text-center text-white font-bold text-base tabular-nums" data-testid={`${testId}-value`}>{value}</span>
    <button type="button" onClick={() => onChange(value + 1)} disabled={value >= 20}
      className="w-10 h-10 rounded-lg bg-gray-700 border border-gray-600 text-white flex items-center justify-center active:bg-gray-600 disabled:opacity-40 touch-manipulation"
      aria-label="Flere serier" data-testid={`${testId}-plus`}>
      <Plus className="w-4 h-4" />
    </button>
  </div>
);

const StartPoint = ({ entry, variant, onChange }) => {
  const id = entry.member_id;
  if (!entry.variant_key) return <p className="text-xs text-gray-500 italic">Velg øvelse for å se startpunkt</p>;
  if (entry.statusLoading) {
    return <p className="text-xs text-gray-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Sjekker tidligere merkeskyting…</p>;
  }
  const st = entry.status?.variants?.[`${entry.discipline_key}:${entry.variant_key}`];
  if (st?.completed) {
    return (
      <p className="text-xs text-yellow-300 flex items-center gap-1" data-testid={`entry-start-auto-${id}`}>
        <Trophy className="w-3.5 h-3.5" />Alle nivåer fullført på denne øvelsen
      </p>
    );
  }
  if (st) {
    return (
      <p className="text-xs text-green-300 flex items-center gap-1 flex-wrap" data-testid={`entry-start-auto-${id}`}>
        <RotateCw className="w-3.5 h-3.5" />
        Fortsetter: <strong className="text-white">{st.active_level_name} {st.count}/10</strong>
        <span className="text-gray-400">(krav {st.threshold} {st.unit_label || variant?.unit_label || 'p'})</span>
      </p>
    );
  }
  return (
    <div className="space-y-1.5" data-testid={`entry-start-choice-${id}`}>
      <span className="block text-xs text-gray-400">Første gang på denne øvelsen – start på:</span>
      <div className="grid grid-cols-2 gap-2">
      {START_OPTIONS.map(opt => {
        const active = entry.start_level === opt.key;
        return (
          <button key={opt.key} type="button" onClick={() => onChange({ start_level: opt.key })}
            className={`px-3 h-10 rounded-lg border text-sm font-medium touch-manipulation transition-colors ${active ? 'bg-yellow-500 border-yellow-400 text-gray-900' : 'bg-gray-800 border-gray-600 text-gray-200'}`}
            data-testid={`entry-start-${opt.key}-${id}`}>
            {opt.label}
            {variant?.requirements?.[opt.key] != null && <span className={`ml-1 text-[10px] ${active ? 'text-gray-800' : 'text-gray-500'}`}>({variant.requirements[opt.key]})</span>}
          </button>
        );
      })}
      </div>
    </div>
  );
};

export const MerkeRegisterEntryCard = ({ entry, activeVariants, onChange, onRemove, onScoreClick }) => {
  const id = entry.member_id;
  const variant = activeVariants.find(v => v.discipline_key === entry.discipline_key && v.variant_key === entry.variant_key);
  const setSeries = (n) => onChange({ scores: resizeScores(entry.scores, n) });
  const sim = simulateSession(entry, variant);
  const thresholds = sim.thresholds;
  return (
    <div className="rounded-xl bg-gray-900/60 border border-gray-700 p-3 space-y-3" data-testid={`register-entry-${id}`}>
      <div className="flex items-center gap-2">
        <p className="flex-1 min-w-0 text-white font-semibold leading-tight line-clamp-2 break-words" data-testid={`entry-name-${id}`}>{entry.member_name}</p>
        <SeriesStepper value={entry.scores.length} onChange={setSeries} testId={`entry-series-${id}`} />
        <button type="button" onClick={onRemove}
          className="w-10 h-10 shrink-0 rounded-lg text-red-400 hover:bg-red-500/10 flex items-center justify-center touch-manipulation"
          aria-label="Fjern medlem" data-testid={`remove-entry-${id}`}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <div className="space-y-2">
        <select
          className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 h-10 text-white text-sm"
          value={`${entry.discipline_key || ''}:${entry.variant_key || ''}`}
          onChange={(e) => { const [d, v] = e.target.value.split(':'); onChange({ discipline_key: d || '', variant_key: v || '' }); }}
          data-testid={`entry-variant-select-${id}`}
        >
          <option value=":">Velg øvelse…</option>
          {activeVariants.map(v => (
            <option key={`${v.discipline_key}:${v.variant_key}`} value={`${v.discipline_key}:${v.variant_key}`}>
              {v.discipline_name} – {v.variant_name}
            </option>
          ))}
        </select>
        <input type="text" value={entry.program || ''} onChange={(e) => onChange({ program: e.target.value })}
          placeholder="Program (valgfritt)" maxLength={60}
          className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 h-9 text-white text-sm placeholder-gray-500"
          data-testid={`entry-program-input-${id}`} />
        <StartPoint entry={entry} variant={variant} onChange={onChange} />
        <BadgeCountdown sim={sim} id={id} />
      </div>

      <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-10 gap-2">
        {entry.scores.map((s, i) => {
          const thr = thresholds[i];
          const filled = s !== '' && s != null;
          const passed = filled && thr != null && Number(s) >= thr;
          const state = !filled ? 'empty' : thr == null ? 'neutral' : passed ? 'pass' : 'fail';
          const cls = {
            empty: 'bg-gray-800 border-gray-600 text-gray-500',
            neutral: 'bg-gray-700 border-gray-500 text-white',
            pass: 'bg-green-600/30 border-green-500 text-green-100',
            fail: 'bg-red-600/30 border-red-500 text-red-100',
          }[state];
          return (
            <button key={i} type="button" onClick={() => onScoreClick(i)}
              className={`h-12 rounded-lg border text-center text-sm font-semibold touch-manipulation transition-colors ${cls}`}
              data-testid={`score-${id}-${i}`} data-state={state}>
              <span className="block text-[9px] font-normal opacity-70 leading-none mb-0.5 whitespace-nowrap">
                S{i + 1}{thr != null ? ` · krav ${thr}` : ''}
              </span>
              {filled ? s : '—'}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const BulkSeriesBar = ({ value, onChange, onApply }) => (
  <div className="flex items-center gap-2 flex-wrap p-3 rounded-xl bg-gray-700/40 border border-gray-600" data-testid="bulk-series-bar">
    <span className="text-sm text-gray-300 font-medium">Serier for alle:</span>
    <SeriesStepper value={value} onChange={(n) => onChange(Math.max(1, Math.min(20, n)))} testId="bulk-series" />
    <button type="button" onClick={onApply}
      className="ml-auto px-4 h-10 rounded-lg bg-yellow-500 text-gray-900 font-semibold text-sm hover:bg-yellow-400 touch-manipulation"
      data-testid="bulk-series-apply-btn">
      Bruk på alle
    </button>
  </div>
);

export { resizeScores };
export default MerkeRegisterEntryCard;
